/**
 * The BD questionnaire, driven by the decision tree.
 *
 * The question list is not a list: it is the result of walking the saved document against the
 * answers so far. See `flow.ts` for the walk and `evaluate.ts` for the conditions.
 *
 * ## Where the tree comes from today
 *
 * `toDocument(SEED_BY_ID['q_all'])` — the canvas's own test data, converted by the same
 * function the admin surface would send. Not the mock API's stored version yet, because the
 * BD app would then need its own fork/read path; the point of this step is the walk, and
 * reading from the API changes nothing about it.
 *
 * ## Answers that stop counting
 *
 * Changing an earlier answer can replace the whole branch below it. The answers collected down
 * the old branch are still in the map, still read by eligibility rules, and therefore able to
 * grant a program on the strength of a question nobody is being asked any more. The flow
 * reports them as `orphanedAnswers` and they are dropped HERE, in the same update that writes
 * the new answer — not in an effect afterwards, because between those two renders the page
 * would be showing a path computed from answers it is about to discard.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-react';

import { useChatPanel } from '../../context/ChatPanelContext';

import { SEED_BY_ID } from '../fixtures/questionnaires';
import { toDocument } from '../utils/wire';
import AnswerBody from './answers/AnswerBody';
import type { AnswerGate, AnswerValue, Answers } from './answerTypes';
import { isAnswered } from './answerTypes';
import { computeFlow, pruneAnswers } from './flow';
import QuestionCard from './QuestionCard';
import './QuestionnairePage.css';

import type { DocQuestion } from '../utils/wire';

/** The tree being walked. Built once: it does not depend on the answers. */
const TREE_ID = 'q_all';

/** One line describing what was chosen, for a collapsed answered card. */
function summarise(q: DocQuestion, v: AnswerValue | undefined): string {
  if (!isAnswered(v)) return '';
  if (q.kind === 'MULTI_SELECT' && Array.isArray(v)) {
    return v.map((x) => q.options?.find((o) => o.optionValue === x)?.label.en ?? x).join('、');
  }
  if (q.kind === 'SINGLE_SELECT') {
    return q.options?.find((o) => o.optionValue === v)?.label.en ?? String(v);
  }
  if (q.kind === 'NUMBER') {
    return `${v}${q.numberFormat?.unit ? ` ${q.numberFormat.unit}` : ''}`;
  }
  return String(v);
}

export default function QuestionnairePage() {
  const { document: doc } = useMemo(() => {
    const r = toDocument(SEED_BY_ID[TREE_ID], {});
    /*
     * The eligibility gap goes to the console, not the page.
     *
     * 16 of the 30 live eligibility rules cannot be structured yet, so what a submit would
     * compute is incomplete -- but that is a fact about the document model, not something the
     * BD filling the form can act on, and a permanent warning banner on their screen is
     * noise. The WALK is unaffected: all 32 transitions convert, so the questions and their
     * order are right.
     */
    if (r.unconvertible.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[CPA] ${r.unconvertible.length} 条资格规则无法结构化，Submit 的计算结果会不完整`,
        r.unconvertible,
      );
    }
    return r;
  }, []);

  /**
   * CONFIRMED answers. The walk runs against these alone.
   *
   * Separate from the value being typed, because the design has a Next button and that button
   * has to mean something: if answering advanced the walk by itself, the next question would
   * appear while the BD was still choosing, and a half-typed number would count as an answer
   * and move the path under them.
   */
  const [answers, setAnswers] = useState<Answers>({});

  /**
   * Uncommitted drafts, keyed by question id — one per open card, not one in total.
   *
   * There is more than one open card: `state` makes a card `current` when it is the walk's
   * current question OR when it has been reopened, and every `current` card renders an
   * editable control. A single `{ id, value }` slot therefore held the draft of whichever card
   * was touched last, and `valueOf` fell back to the COMMITTED answer for all the others — so
   * reopening question 2 and then touching the open question 6 silently threw away the edit to
   * 2, with no indication that anything had been dropped.
   *
   * `id in pending` rather than a truthiness test, because `undefined` is a meaningful draft:
   * it is what clearing an answer produces, and it has to win over the committed value.
   */
  const [pending, setPending] = useState<Record<string, AnswerValue | undefined>>({});

  /** The answers whose cards are open for editing again. */
  const [reopened, setReopened] = useState<Set<string>>(new Set());

  /**
   * A change to an earlier answer that is waiting for confirmation, with the exact questions
   * it would take off the path.
   *
   * Computed by running the walk against the prospective answer, so the list is what WILL
   * happen rather than a general warning that something might. A vague "later answers may be
   * affected" teaches people to click through it.
   */
  const [confirming, setConfirming] = useState<{
    id: string;
    value: AnswerValue | undefined;
    dropped: string[];
  } | null>(null);

  const flow = useMemo(() => computeFlow(doc, answers), [doc, answers]);

  /*
   * Checks Next has to pass, registered by the controls that have one.
   *
   * Only SFDC does today, and it needs a round trip: "is this a real account" is not derivable
   * from the typed value. Before this existed the control offered its own 校验 button and
   * `nextDisabled` only asked whether the field was non-empty — so an unverified id could walk
   * the entire questionnaire and reach the submission. The live AgentHub validates inside its
   * Next handler and refuses to advance; this is the same arrangement.
   *
   * In refs, not state: registering a gate must not re-render, and the gate is read at the
   * moment Next is pressed rather than during render.
   */
  const gates = useRef<Map<string, AnswerGate>>(new Map());
  const registrars = useRef<Map<string, (g: AnswerGate | null) => void>>(new Map());

  /**
   * One STABLE registrar per question id.
   *
   * Cached because the control registers in an effect keyed on this function: handing out a
   * fresh closure each render would unregister and re-register on every keystroke elsewhere on
   * the page, and a cleanup that lands between the two is a window where Next has no gate.
   */
  const registrarFor = useCallback((questionId: string) => {
    let r = registrars.current.get(questionId);
    if (!r) {
      r = (g: AnswerGate | null) => {
        if (g) gates.current.set(questionId, g);
        else gates.current.delete(questionId);
      };
      registrars.current.set(questionId, r);
    }
    return r;
  }, []);

  /** The question whose gate is running, so its Next can say so instead of looking dead. */
  const [gateBusy, setGateBusy] = useState<string | null>(null);

  /** Typing. Touches nothing the walk reads. */
  const editAnswer = useCallback((id: string, v: AnswerValue | undefined) => {
    setPending((prev) => ({ ...prev, [id]: v }));
  }, []);

  /**
   * Write a confirmed answer, dropping the ones it strands.
   *
   * Answers for questions the new path does not reach are DELETED, not archived. Keeping them
   * was tried and reverted: it bought one thing — changing an answer back restored the branch
   * without retyping — and cost the invariant that `answers` is the submission. Anything
   * reading the map instead of the walk would silently include answers to questions nobody is
   * being asked, and eligibility rules read the map. That is the same class of defect as the
   * live `ref-not-dominating` bug, where a rule read an answer that does not exist on the
   * path.
   *
   * The retype it saved was never the real risk anyway: a branch switch is already guarded by
   * a confirmation that names every question it removes, so changing back is a deliberate
   * second act rather than an accident worth insuring against.
   *
   * Pruned in the SAME update as the write. In an effect afterwards there would be one render
   * showing a path computed from answers that are about to be discarded.
   */
  const writeAnswer = useCallback(
    (id: string, v: AnswerValue | undefined) => {
      setAnswers((prev) => {
        const next = { ...prev, [id]: v };
        return pruneAnswers(next, computeFlow(doc, next).orphanedAnswers);
      });
      // Only this card's draft. Clearing the whole map would discard the drafts of any
      // other reopened card still open beside it.
      setPending((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setReopened((prev) => {
        if (!prev.has(id)) return prev;
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
    },
    [doc],
  );

  /**
   * Next. Validates first, then asks when the change would replace questions further down.
   *
   * The gate runs BEFORE the branch-change check on purpose: a rejected answer must not
   * produce a dialog asking whether to discard later questions, since nothing is going to
   * change. It also runs before `writeAnswer`, so a failed check leaves `answers` untouched.
   */
  const commitAnswer = useCallback(
    async (id: string) => {
      const v = id in pending ? pending[id] : answers[id];
      if (!isAnswered(v)) return;

      const gate = gates.current.get(id);
      if (gate) {
        setGateBusy(id);
        let ok: boolean;
        try {
          ok = await gate();
        } finally {
          setGateBusy(null);
        }
        // The control shows WHY — the message belongs next to the field it is about.
        if (!ok) return;
      }

      // What the path would become. Everything currently answered that the new path does not
      // reach is a question the BD would stop being asked.
      const after = computeFlow(doc, { ...answers, [id]: v });
      const stillOnPath = new Set([
        ...after.answered.map((x) => x.question.questionId),
        ...(after.current ? [after.current.question.questionId] : []),
      ]);
      const dropped = flow.answered
        .map((x) => x.question.questionId)
        .filter((qid) => qid !== id && !stillOnPath.has(qid));

      if (dropped.length > 0) {
        setConfirming({ id, value: v, dropped });
        return;
      }
      writeAnswer(id, v);
    },
    [doc, pending, answers, flow, writeAnswer],
  );

  /** What a card shows: the pending value while it is being answered, else the committed one. */
  const valueOf = (id: string) => (id in pending ? pending[id] : answers[id]);

  /*
   * `*NOTES` goes to the portal's chat, not to a panel of our own.
   *
   * `sendPrompt` both opens the panel and fires a request, so the note arrives as a message
   * and the agent answers about it — which is what the design shows and what makes the link
   * worth pressing. `prefillPrompt` exists too and only fills the composer; it is the wrong
   * one here, because then nothing would happen until the BD also pressed send.
   *
   * The question is named in the message. In a transcript, a wall of "NOTES:" text with no
   * subject is unreadable two messages later, and the agent needs it to answer follow-ups.
   */
  const { sendPrompt } = useChatPanel();

  const openNotes = (q: DocQuestion) => {
    const note = q.note;
    if (!note) return undefined;
    return () =>
      sendPrompt(
        [
          `关于问卷题目「${q.prompt.zh ?? q.prompt.en}」的说明：`,
          note.en,
          note.zh,
          '',
          '请结合这段说明，帮我理解这道题要填什么。',
        ]
          .filter(Boolean)
          .join('\n'),
      );
  };

  /*
   * The rendered list: answered, then the current one, then the preview.
   *
   * The preview is the walk's guess — it takes the first branch at each step because the
   * answers that would decide it do not exist yet. Marked as such below, since presenting a
   * guess as a plan is how a step counter ends up going backwards.
   */
  const rows = [
    ...flow.answered.map((s) => ({ step: s, kind: 'answered' as const })),
    ...(flow.current ? [{ step: flow.current, kind: 'current' as const }] : []),
    ...flow.preview.map((s) => ({ step: s, kind: 'preview' as const })),
  ];

  const answeredCount = flow.answered.length;

  return (
    <div className="cpa-bd">
      <header className="cpa-bd__hero">
        <h1>Credit Program Advisor</h1>
        <p>
          Credit Program Advisor is designed to provide account team with a clear path of
          qualified AWS pricing &amp; investment resources, e.g. private pricing, investment
          programs, when facing competition. You may need to answer a maximum of 15 questions
          depending on competition support complexity.
        </p>
      </header>

      {/*
        The questionnaire column.
        Narrower than the hero and centred independently of it, which is what gives it the
        larger side margin the design asks for -- rather than padding it inward from a shared
        column, where the gap would be unequal the moment the viewport changed.
      */}
      <div className="cpa-bd__col">
      {/*
        Answered count, not "3 of 15".
        A decision tree has no total: the number of remaining questions depends on answers not
        yet given, and the live trees range from 1 question to 15. A denominator would have to
        change as the BD answers, which reads as the form growing while you fill it in.
      */}
      <div className="cpa-bd__progress">
        已回答 {answeredCount} 题
        {flow.done && ' · 已到问卷终点'}
      </div>

      {flow.blocked && (
        <div className="cpa-bd__blocked">
          问卷走不下去了，这是决策树的配置问题，不是你的操作问题：
          {flow.blocked.reason === 'no-branch' && (
            <>
              {' '}
              <code>{flow.blocked.questionId}</code> 的所有出边条件都不成立，没有分支可走。
            </>
          )}
          {flow.blocked.reason === 'undecidable' && (
            <>
              {' '}
              <code>{flow.blocked.transitionId}</code> 的条件引用了这条路径上没有作答的问题。
            </>
          )}
          {flow.blocked.reason === 'dangling' && (
            <>
              {' '}
              <code>{flow.blocked.transitionId}</code> 指向不存在的问题{' '}
              <code>{flow.blocked.toQuestionId}</code>。
            </>
          )}
          {flow.blocked.reason === 'cycle' && (
            <>
              {' '}
              路径回到了已经走过的 <code>{flow.blocked.questionId}</code>，构成环。
            </>
          )}
        </div>
      )}

      <div className="cpa-bd__list">
        {rows.map(({ step, kind }, i) => {
          const q = step.question;
          const v = valueOf(q.questionId);
          const isReopened = reopened.has(q.questionId);
          const state = kind === 'preview' ? 'upcoming' : isReopened || kind === 'current' ? 'current' : 'answered';
          return (
            <QuestionCard
              key={q.questionId}
              ordinal={i + 1}
              state={state}
              prompt={q.prompt.en}
              promptZh={q.prompt.zh}
              answerSummary={summarise(q, v)}
              speculative={kind === 'preview'}
              onOpenNotes={openNotes(q)}
              onReopen={
                kind === 'answered' && !isReopened
                  ? () => {
                      // Seed the draft from the committed answer, so reopening shows what was
                      // chosen rather than an empty control, and Next re-commits it.
                      setPending((prev) => ({ ...prev, [q.questionId]: answers[q.questionId] }));
                      setReopened((prev) => new Set(prev).add(q.questionId));
                    }
                  : undefined
              }
              /*
               * The dot is the other way back. Same action as clicking the answer text, on the
               * timeline, because the timeline is what a reader scans to find "where was that
               * question about the PPA" -- the answer text is only findable once you are
               * already looking at the right card.
               */
              onSelectDot={
                kind === 'answered'
                  ? () => {
                      setPending((prev) => ({ ...prev, [q.questionId]: answers[q.questionId] }));
                      setReopened((prev) => new Set(prev).add(q.questionId));
                    }
                  : undefined
              }
              isLast={i === rows.length - 1}
              /*
               * Next is what advances the walk, so it exists on the open card only. Disabled
               * until there is something to commit -- the flow cannot choose a branch from an
               * empty answer, so a Next that fired anyway would have to either do nothing or
               * guess.
               */
              onNext={state === 'current' ? () => void commitAnswer(q.questionId) : undefined}
              nextDisabled={!isAnswered(v)}
              nextBusy={gateBusy === q.questionId}
            >
              <AnswerBody
                question={q}
                value={v}
                onChange={(next) => editAnswer(q.questionId, next)}
                /*
                 * Only the open card's control may gate Next. A reopened answered card is also
                 * `current`, so it gets one too — correct, since its answer is being committed
                 * again. Preview cards never are.
                 */
                registerGate={state === 'current' ? registrarFor(q.questionId) : undefined}
              />
            </QuestionCard>
          );
        })}
      </div>

      <div className="cpa-bd__submit-row">
        <button type="button" className="cpa-bd__submit" disabled={!flow.done}>
          Submit
        </button>
      </div>
      </div>

      {/*
        The consequence, stated before it happens.
        Naming the questions rather than counting them: "3 questions will be removed" is a
        number nobody can check, while the prompts are what the BD recognises as work they
        already did.
      */}
      <Modal
        visible={!!confirming}
        onDismiss={() => setConfirming(null)}
        header="改这个答案会换掉后面的题"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setConfirming(null)}>取消</Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (confirming) writeAnswer(confirming.id, confirming.value);
                  setConfirming(null);
                }}
              >
                确认修改
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {confirming && (
          <SpaceBetween size="s">
            <Box>
              新答案会走另一条分支，下面这{confirming.dropped.length}道题不再出现在问卷里：
            </Box>
            <ul className="cpa-bd__dropped">
              {confirming.dropped.map((qid) => {
                const q = doc.questions.find((x) => x.questionId === qid);
                return (
                  <li key={qid}>
                    <code>{qid}</code> {q?.prompt.zh ?? q?.prompt.en}
                  </li>
                );
              })}
            </ul>
            <Box variant="small" color="text-body-secondary">
              它们的答案会被清除。如果之后再走回这条分支，需要重新填一次。
            </Box>
          </SpaceBetween>
        )}
      </Modal>
    </div>
  );
}
